import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, CheckCircle2, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { http, streamSSE } from "@/lib/api";
import { NarrationButton } from "@/components/lesson/NarrationButton";

export default function PathwayLesson() {
  const { track, subjectId, unit } = useParams();
  const nav = useNavigate();
  const [text, setText] = useState("");
  const [state, setState] = useState("loading");
  const run = useRef(0);
  const back = track === "ged" ? `/pathway/ged/${subjectId}` : "/pathway";
  const key = track === "ged" ? `${subjectId}:${unit}` : `biz:${unit}`;

  const load = async () => {
    const id = ++run.current;
    setText(""); setState("loading");
    try {
      await streamSSE("/pathway/lesson/stream", { track, subject_id: subjectId, unit: Number(unit) }, (d) => { if (id === run.current) setText((t) => t + d); });
      if (id === run.current) setState("ready");
    } catch { if (id === run.current) setState("error"); }
  };
  useEffect(() => { load(); }, [track, subjectId, unit]); // eslint-disable-line react-hooks/exhaustive-deps

  const complete = async () => {
    try { await http.post("/pathway/complete", { key }); toast.success("Lesson complete!"); nav(back); }
    catch { toast.error("Couldn't save your progress. Please try again."); }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 py-12 pb-24" data-testid="pathway-lesson-page">
      <Link to={back} className="inline-flex items-center gap-2 text-sm text-stone-500 hover:text-[#2C4A3E]" data-testid="pathway-lesson-back"><ArrowLeft className="w-4 h-4" /> Back</Link>
      <div className="flex flex-wrap gap-3 mt-6">
        {state === "ready" && <NarrationButton text={text} />}
        {state !== "loading" && <button onClick={load} data-testid="pathway-lesson-regenerate" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-[#E6DEC3] text-sm text-[#2C4A3E]"><RefreshCw className="w-4 h-4" /> New version</button>}
      </div>
      {state === "error" && <p className="mt-8 text-red-700" data-testid="pathway-lesson-error">Couldn't load this lesson. Please try again.</p>}
      <article className="lesson-md mt-8 prose-stone" data-testid="pathway-lesson-content">
        {text ? <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown> : state === "loading" && <p className="text-stone-500 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Preparing your lesson…</p>}
      </article>
      {state === "ready" && (
        <button onClick={complete} data-testid="pathway-lesson-complete-btn" className="mt-10 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#2C4A3E] text-white hover:bg-[#1E352B]">
          <CheckCircle2 className="w-4 h-4" /> Mark complete
        </button>
      )}
    </div>
  );
}
